import { useConditional } from "../core/hooks/useConditional";
import { closestKey } from "../core/utils/dom";
import { FormModal } from "../data-entry/FormModal";
import { WorkflowState, wf } from "../types";
import { Edge, Graph, Node, layoutNodes } from "./layout";


function WorkflowLayout(props: {
	wf: wf,

	onEditState: (state: WorkflowState) => void,
}) {

	const def = props.wf.data;

	const edges: Edge[] = [];
	const nodes: Node[] = [];
	const nMap = {} as { [k: string]: Node };
	const stateMap = {} as { [k: string]: WorkflowState };

	// Collect all transition info for the layout algorithm
	const transitionInfos: { sourceId: string, targetId: string, isDefault: boolean }[] = [];

	for (let s of def.states) {
		stateMap[s.id] = s;

		const n = {
			id: s.id,
			x: 0,
			y: 0,
			w: 160,
			h: 50,
		} as Node;
		nodes.push(n);
		nMap[s.id] = n;
	}

	// Build edges and transition info
	const edgesProcessed = {} as { [k: string]: boolean };
	for (let s of def.states) {
		for (let t of s.transitions) {
			const source = s.id;
			const target = t.toState;
			if (nMap[source] && nMap[target]) {
				transitionInfos.push({
					sourceId: source,
					targetId: target,
					isDefault: !!t.isDefault,
				});

				// Only create one edge per pair of states (deduplicate A->B and B->A)
				const fwdKey = source + "-" + target;
				const revKey = target + "-" + source;
				if (!edgesProcessed[fwdKey] && !edgesProcessed[revKey]) {
					edges.push({
						source: source,
						target: target,
						routingPoints: [],
					});
					edgesProcessed[fwdKey] = true;
				}
			}
		}
	}

	const graph: Graph = {
		nodes,
		edges,
		width: 800,
		height: 600,
	}

	layoutNodes(graph, transitionInfos);

	// Render nodes
	const elNodes: any[] = [];
	const lineHeight = 16;
	const padding = 10;

	for (let n of graph.nodes) {
		const state = stateMap[n.id];
		const isBackbone = n.column === 0;

		// Estimate chars per line based on node width and approximate char width
		const avgCharWidth = isBackbone ? 8.5 : 7.5;
		const maxCharsPerLine = Math.floor((n.w - padding * 2) / avgCharWidth);
		const lines = wrapText(state.title, maxCharsPerLine);

		// Adjust node height if text needs multiple lines
		const textHeight = lines.length * lineHeight;
		const minHeight = textHeight + padding * 2;
		if (minHeight > n.h) {
			n.h = minHeight;
		}

		const cx = n.x + n.w / 2;
		const cy = n.y + n.h / 2;

		const phaseColors: { [k: string]: string } = {
			"complete": "#41a200",
			"attention": "#dc3832",
			"in progress": "#1085c2",
		};
		const borderColor = phaseColors[(state.phase || "").toLowerCase()] || "#888";
		const bgColor = lightenColor(borderColor, isBackbone ? 0.1 : 0.5);

		// Position text lines centered vertically
		const textStartY = cy - ((lines.length - 1) * lineHeight) / 2;

		elNodes.push(
			<g className="state" data-key={state.id} key={state.id}>
				<rect x={n.x} y={n.y} width={n.w} height={n.h} rx={6} ry={6} className={isBackbone ? "is-fixed" : undefined} style={{ stroke: borderColor, strokeWidth: "1px", fill: bgColor }} />,
				<text x={cx} className={isBackbone ? "is-fixed" : undefined} style={isBackbone ? { fontSize: "calc(1em + 3px)" } : undefined}>
					{lines.map((line: string, li: number) => (
						<tspan x={cx} y={textStartY + li * lineHeight} dominantBaseline="central" key={li}>{line}</tspan>
					))}
				</text>
			</g>
		);

		nMap[n.id] = n;
	}

	// Render edges with arrow heads
	const elLines: any[] = [];
	let i = 0;
	for (let e of graph.edges) {
		const nSrc = nMap[e.source];
		const nDest = nMap[e.target];

		const isBidirectional = e.isBidirectional;

		const arrowSizeStart = isBidirectional ? 15 : 0;
		const arrowSizeEnd = 15;

		const d = getCurvePath(nSrc, nDest, graph.nodes, arrowSizeStart, arrowSizeEnd);
		if (!d) { i++; continue; }

		elLines.push(
			<path d={d} key={i} className="rel"
				markerStart={isBidirectional ? "url(#markerArrowStart)" : undefined}
				markerEnd="url(#markerArrowEnd)"
			/>
		);
		i++;
	}

	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={graph.width}
			height={graph.height}

			className="wf-designer"

			onClick={(e: any) => {
				const key = closestKey(e.target, ".state");
				if (!key) return;
				const state = stateMap[key];
				props.onEditState(state);
			}}
		>
			<defs>
				<marker id="markerArrowStart" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
					<path d="M9,0 L9,6 L0,3 z" fill="#aaa" />
				</marker>

				<marker id="markerArrowEnd" markerWidth="10" markerHeight="10" refX="2" refY="3" orient="auto">
					<path d="M2,0 L2,6 L10,3 z" style={{ fill: "#aaa" }} />
				</marker>
			</defs>

			{elLines}
			{elNodes}

		</svg>
	);

}


export function Designer(props: {
	wf: wf,
}) {


	const editModal = useConditional((state: WorkflowState) => {
		return (
			<FormModal
				obj={state}
				title={`Edit State - ${state.title}`}
				form="workflow-state-edit"
				size="width-75vw"
				onCancel={editModal.close}
				onSave={(value: WorkflowState) => {
					editModal.close();
				}}
				showDelete={true}
				onDelete={() => {
					const idx = props.wf.data.states.indexOf(state);
					if (idx >= 0) {
						props.wf.data.states.splice(idx, 1);
						// Remove transitions pointing to the deleted state
						for (const s of props.wf.data.states) {
							s.transitions = s.transitions.filter(t => t.toState !== state.id);
						}
					}
					editModal.close();
				}}
			/>
		)
	});


	return (
		<div>

			<WorkflowLayout
				wf={props.wf}
				onEditState={(state: WorkflowState) => {
					editModal.show(state);
				}}
			/>

			{editModal.render()}

		</div>

	);
}



function wrapText(text: string, maxChars: number): string[] {
	if (text.length <= maxChars) return [text];
	const words = text.split(" ");
	const lines: string[] = [];
	let current = "";
	for (const word of words) {
		if (current && (current.length + 1 + word.length) > maxChars) {
			lines.push(current);
			current = word;
		} else {
			current = current ? current + " " + word : word;
		}
	}
	if (current) lines.push(current);
	return lines.length > 0 ? lines : [text];
}

function lightenColor(hex: string, amount: number): string {
	// Expand shorthand #RGB to #RRGGBB
	let h = hex.replace("#", "");
	if (h.length === 3) {
		h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
	}
	const r = parseInt(h.slice(0, 2), 16);
	const g = parseInt(h.slice(2, 4), 16);
	const b = parseInt(h.slice(4, 6), 16);
	const lr = Math.round(r + (255 - r) * amount);
	const lg = Math.round(g + (255 - g) * amount);
	const lb = Math.round(b + (255 - b) * amount);
	const toHex = (v: number) => v.toString(16).padStart(2, "0");
	return "#" + toHex(lr) + toHex(lg) + toHex(lb);
}

type Side = "left" | "right" | "top" | "bottom";

function getConnectionPoint(node: Node, side: Side) {
	const cx = node.x + node.w / 2;
	const cy = node.y + node.h / 2;
	switch (side) {
		case "top": return { x: cx, y: node.y, nx: 0, ny: -1 };
		case "bottom": return { x: cx, y: node.y + node.h, nx: 0, ny: 1 };
		case "left": return { x: node.x, y: cy, nx: -1, ny: 0 };
		case "right": return { x: node.x + node.w, y: cy, nx: 1, ny: 0 };
	}
}

function pickSides(n1: Node, n2: Node): [Side, Side] {
	const dx = (n2.x + n2.w / 2) - (n1.x + n1.w / 2);
	const dy = (n2.y + n2.h / 2) - (n1.y + n1.h / 2);

	if (Math.abs(dy) > Math.abs(dx)) {
		return dy > 0 ? ["bottom", "top"] : ["top", "bottom"];
	}
	return dx > 0 ? ["right", "left"] : ["left", "right"];
}

function getCurvePath(
	node1: Node,
	node2: Node,
	allNodes: Node[],
	arrowSizeStart = 10,
	arrowSizeEnd = 10,
) {
	const c1x = node1.x + node1.w / 2;
	const c1y = node1.y + node1.h / 2;
	const c2x = node2.x + node2.w / 2;
	const c2y = node2.y + node2.h / 2;
	if (c1x === c2x && c1y === c2y) return '';

	const sameColumn = node1.column === node2.column;

	// For same-column connections with intermediate nodes in between, detour around them
	if (sameColumn && node1.row !== undefined && node2.row !== undefined) {
		const minRow = Math.min(node1.row, node2.row);
		const maxRow = Math.max(node1.row, node2.row);
		const col = node1.column || 0;

		let hasBlocker = false;
		for (const n of allNodes) {
			if (n.id === node1.id || n.id === node2.id) continue;
			if (n.column === col && n.row !== undefined && n.row > minRow && n.row < maxRow) {
				hasBlocker = true;
				break;
			}
		}

		if (hasBlocker) {
			// Count nodes on left vs right between these rows to pick the clearer side
			let leftCount = 0, rightCount = 0;
			for (const n of allNodes) {
				if (n.row !== undefined && n.row >= minRow && n.row <= maxRow) {
					if (n.column !== undefined && n.column > col) rightCount++;
					if (n.column !== undefined && n.column < col) leftCount++;
				}
			}

			const goRight = rightCount <= leftCount;
			const detourOffset = 50;
			let detourX: number;
			let exitSide: Side;

			if (goRight) {
				detourX = Math.max(node1.x + node1.w, node2.x + node2.w) + detourOffset;
				exitSide = "right";
			} else {
				detourX = Math.min(node1.x, node2.x) - detourOffset;
				exitSide = "left";
			}

			const s = getConnectionPoint(node1, exitSide);
			const e = getConnectionPoint(node2, exitSide);

			const sx = s.x + s.nx * arrowSizeStart;
			const sy = s.y;
			const ex = e.x + e.nx * arrowSizeEnd;
			const ey = e.y;

			return `M${sx} ${sy} C ${detourX} ${sy} ${detourX} ${ey} ${ex} ${ey}`;
		}
	}

	// Standard connection: pick sides based on relative position
	const [s1, s2] = pickSides(node1, node2);
	const start = getConnectionPoint(node1, s1);
	const end = getConnectionPoint(node2, s2);

	const sx = start.x + start.nx * arrowSizeStart;
	const sy = start.y + start.ny * arrowSizeStart;
	const ex = end.x + end.nx * arrowSizeEnd;
	const ey = end.y + end.ny * arrowSizeEnd;

	const pathDist = Math.hypot(ex - sx, ey - sy);
	const offset = Math.max(pathDist / 3, 20);

	const cx1 = sx + start.nx * offset;
	const cy1 = sy + start.ny * offset;
	const cx2 = ex + end.nx * offset;
	const cy2 = ey + end.ny * offset;

	return `M${sx} ${sy} C ${cx1} ${cy1} ${cx2} ${cy2} ${ex} ${ey}`;
}
